import React, { useEffect, useState, useCallback } from 'react';
import { Project } from '../../types';
import { dossierAPI, projectsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { LockBar } from '../dossier/LockBar';
import { useSectionClear } from '../dossier/useSectionClear';
import { RichText, htmlHasText } from '../common/RichText';
import { useIsMobile } from '../../hooks/useIsMobile';
import { BRAND, faseKleur } from '../../styles/brand';
import { FaseTag } from '../flow/FaseTag';

interface ProjectverkenningModuleProps {
  project: Project;
}

type ContentKey =
  | 'doelstelling'
  | 'maatschappelijkeContext'
  | 'projectorganisatie'
  | 'belangrijksteStakeholders'
  | 'kansenRisicos'
  | 'rolCommunicatie'
  | 'communicatieopgave';

const FIELDS: { key: ContentKey; label: string; hint: string }[] = [
  { key: 'doelstelling', label: 'Doelstelling van het project', hint: 'Wat wil dit project bereiken?' },
  { key: 'maatschappelijkeContext', label: 'Maatschappelijke & bestuurlijke context', hint: 'Welke maatschappelijke en bestuurlijke factoren spelen mee?' },
  { key: 'projectorganisatie', label: 'Projectorganisatie & besluitvorming', hint: 'Wie zijn betrokken en hoe verloopt de besluitvorming?' },
  { key: 'belangrijksteStakeholders', label: 'Belangrijkste stakeholders', hint: 'Welke partijen en groepen worden geraakt of hebben invloed?' },
  { key: 'kansenRisicos', label: "Kansen, risico's & gevoeligheden", hint: 'Wat kan meezitten of tegenzitten? Welke gevoeligheden liggen er?' },
  { key: 'rolCommunicatie', label: 'Rol van communicatie bij de projectdoelen', hint: 'Welke bijdrage moet communicatie leveren aan het projectsucces?' },
];

const emptyContent: Record<ContentKey, string> = {
  doelstelling: '',
  maatschappelijkeContext: '',
  projectorganisatie: '',
  belangrijksteStakeholders: '',
  kansenRisicos: '',
  rolCommunicatie: '',
  communicatieopgave: '',
};

const cardStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, background: '#fff',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 2,
};

const hintStyle: React.CSSProperties = { fontSize: 12.5, color: '#8a90a0', marginBottom: 10 };

function textareaStyle(disabled: boolean): React.CSSProperties {
  return {
    width: '100%', minHeight: 84, resize: 'vertical', padding: '10px 12px',
    borderRadius: 8, border: '1px solid #d8dce4', fontSize: 14, lineHeight: 1.55,
    fontFamily: 'inherit', color: '#20242e', background: disabled ? '#f6f7f9' : '#fff',
    boxSizing: 'border-box',
  };
}

export const ProjectverkenningModule: React.FC<ProjectverkenningModuleProps> = ({ project }) => {
  const toast = useToast();
  const isMobile = useIsMobile();
  const [content, setContent] = useState<Record<ContentKey, string>>(emptyContent);
  const [status, setStatus] = useState<'open' | 'locked'>('open');
  const [lockedAt, setLockedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locking, setLocking] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [ai, setAi] = useState<{ verhelderingsvragen: string[]; conceptCommunicatieopgave: string } | null>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [verwijderBezig, setVerwijderBezig] = useState<string | null>(null);
  // Uploaden en verwijderen zijn server-side voorbehouden aan beheerders en projectleiders.
  const { isProjectManager: magDocumentenBeheren } = useAuth();

  const locked = status === 'locked';

  const loadDossier = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dossierAPI.getByProject(project.id);
      const section = res?.dossier?.sections?.projectverkenning;
      if (section) {
        const sc: any = section.content || {};
        const { __ai, ...fields } = sc;
        setContent({ ...emptyContent, ...fields });
        if (__ai) {
          setAi({
            verhelderingsvragen: __ai.verhelderingsvragen || [],
            conceptCommunicatieopgave: __ai.conceptCommunicatieopgave || '',
          });
        }
        setStatus(section.status === 'locked' ? 'locked' : 'open');
        setLockedAt(section.lockedAt || null);
      }
    } catch (e) {
      // dossier wordt server-side aangemaakt zodra beschikbaar
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  const loadDocuments = useCallback(async () => {
    try {
      const res = await projectsAPI.getDocuments(project.id);
      setDocuments(res?.documents || res || []);
    } catch (e) {
      setDocuments([]);
    }
  }, [project.id]);

  useEffect(() => {
    loadDossier();
    loadDocuments();
  }, [loadDossier, loadDocuments]);

  // Beheerders kunnen deze fase leegmaken om in een testsessie opnieuw te vullen.
  const { canClear, clearing, doClear } = useSectionClear(project.id, project.name, 'projectverkenning', "Projectverkenning", {}, loadDossier);

  const update = (key: ContentKey, value: string) => {
    setContent((c) => ({ ...c, [key]: value }));
  };

  // Neem het AI-resultaat mee bij elke save/lock, zodat het niet wordt overschreven.
  const withAi = (base: Record<ContentKey, string>) => {
    const p: any = { ...base };
    if (ai) p.__ai = ai;
    return p;
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await dossierAPI.saveSection(project.id, 'projectverkenning', withAi(content));
      toast.success('Concept opgeslagen', 'De projectverkenning is als concept vastgelegd.');
    } catch (e: any) {
      toast.error('Opslaan mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally {
      setSaving(false);
    }
  };

  const runAssist = async () => {
    setAiLoading(true);
    try {
      const res = await dossierAPI.assistProjectverkenning(project.id, content);
      const result = {
        verhelderingsvragen: res?.verhelderingsvragen || [],
        conceptCommunicatieopgave: res?.conceptCommunicatieopgave || '',
      };
      setAi(result);
      // Direct persisteren zodat de vragen zichtbaar blijven na herladen.
      try {
        await dossierAPI.saveSection(project.id, 'projectverkenning', { ...content, __ai: result });
      } catch { /* stil: UI toont het resultaat al */ }
    } catch (e: any) {
      toast.error('AI-ondersteuning mislukt', e?.response?.data?.error || 'Probeer het later opnieuw.');
    } finally {
      setAiLoading(false);
    }
  };

  const applyConcept = () => {
    if (ai?.conceptCommunicatieopgave) {
      update('communicatieopgave', ai.conceptCommunicatieopgave);
      toast.success('Overgenomen', 'Het concept staat nu in de communicatieopgave. Scherp het gerust aan.');
    }
  };

  const canLock = Boolean(htmlHasText(content.doelstelling) && htmlHasText(content.communicatieopgave));

  const doLock = async () => {
    setLocking(true);
    try {
      const res = await dossierAPI.lockSection(project.id, 'projectverkenning', withAi(content));
      setStatus('locked');
      setLockedAt(res?.section?.lockedAt || new Date().toISOString());
      toast.success('Fase gevalideerd', 'Projectverkenning is vastgelegd in het projectdossier.');
    } catch (e: any) {
      toast.error('Valideren mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally {
      setLocking(false);
    }
  };

  const doUnlock = async () => {
    setLocking(true);
    try {
      await dossierAPI.unlockSection(project.id, 'projectverkenning');
      setStatus('open');
      toast.success('Fase heropend', 'Je kunt de projectverkenning nu aanpassen en opnieuw valideren.');
    } catch (e: any) {
      toast.error('Heropenen mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally {
      setLocking(false);
    }
  };

  const documentNaam = (d: any): string => d.originalName || d.name || d.filename || 'Document';

  const verwijderDocument = async (d: any) => {
    const id = d._id || d.id;
    if (!id) return;
    const naam = documentNaam(d);
    if (!window.confirm(`Document "${naam}" verwijderen uit "${project.name}"?\n\nHet bestand wordt ook uit de opslag gehaald. Dit kan niet ongedaan worden gemaakt.`)) return;
    setVerwijderBezig(id);
    try {
      await projectsAPI.deleteDocument(project.id, id);
      toast.success('Document verwijderd', `"${naam}" is losgekoppeld van het project en uit de opslag gehaald.`);
      loadDocuments();
    } catch (e: any) {
      toast.error('Verwijderen mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally {
      setVerwijderBezig(null);
    }
  };

  const onUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    try {
      await projectsAPI.uploadDocuments(project.id, Array.from(files));
      toast.success('Document toegevoegd', 'Het document is aan het project gekoppeld.');
      loadDocuments();
    } catch (e: any) {
      toast.error('Upload mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    }
  };

  if (loading) {
    return <div style={{ padding: 32, color: '#6b7280' }}>Projectdossier laden…</div>;
  }

  return (
    <div style={{ padding: isMobile ? 16 : 32, maxWidth: 1120, margin: '0 auto' }}>
      <FaseTag nr={1} />
      <h1 style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 700, color: BRAND.groen, letterSpacing: '-0.3px' }}>Projectverkenning</h1>
      <p style={{ margin: '0 0 24px', color: '#555', fontSize: 15, lineHeight: 1.6 }}>
        Breng samen met de opdrachtgever de volledige projectopgave in kaart — niet alleen de
        communicatievraag, maar ook de bredere context. Daaruit formuleren we de communicatieopgave,
        het vertrekpunt voor alle volgende fasen van <strong>{project.name}</strong>.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 340px', gap: 20, alignItems: 'start' }}>
        {/* LINKERKOLOM */}
        <div style={{ display: 'grid', gap: 16, minWidth: 0 }}>
      {/* Structurele intake-velden */}
      <div style={{ display: 'grid', gap: 16 }}>
        {FIELDS.map((f) => (
          <div key={f.key} style={cardStyle}>
            <label style={labelStyle}>{f.label}</label>
            <div style={hintStyle}>{f.hint}</div>
            <RichText
              inklapbaar
              value={content[f.key]}
              onChange={(html) => update(f.key, html)}
              disabled={locked}
              placeholder="Vul in…"
            />
          </div>
        ))}
      </div>

      {/* Documenten */}
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div style={labelStyle}>Projectdocumenten</div>
        <div style={hintStyle}>Upload beschikbare stukken (plan, besluit, eerdere communicatie). De AI gebruikt deze als context.</div>
        {documents.length > 0 && (
          <ul style={{ margin: '0 0 12px', padding: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
            {documents.map((d: any, i: number) => {
              const id = d._id || d.id;
              const naam = documentNaam(d);
              return (
                <li key={id || i} style={{ fontSize: 13.5, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                  {d.url
                    ? <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ color: '#374151', textDecoration: 'underline', textUnderlineOffset: 2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{naam}</a>
                    : <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{naam}</span>}
                  {!locked && magDocumentenBeheren && id && (
                    <button
                      onClick={() => verwijderDocument(d)}
                      disabled={verwijderBezig === id}
                      title={`"${naam}" verwijderen`}
                      aria-label={`"${naam}" verwijderen`}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, flexShrink: 0, borderRadius: 6, border: '1px solid #f0c9c9', background: '#fff', color: '#b91c1c', cursor: verwijderBezig === id ? 'default' : 'pointer', opacity: verwijderBezig === id ? 0.5 : 1 }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {!locked && magDocumentenBeheren && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 8, border: '1px dashed #c3c9d4', fontSize: 13.5, color: '#374151', cursor: 'pointer' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
            Document toevoegen
            <input type="file" multiple style={{ display: 'none' }} onChange={(e) => onUpload(e.target.files)} />
          </label>
        )}
      </div>

      {/* Communicatieopgave — de afgeleide output */}
      <div style={{ ...cardStyle, marginTop: 16, borderColor: '#1a1a2e', borderWidth: 1 }}>
        <label style={labelStyle}>De communicatieopgave</label>
        <div style={hintStyle}>Welke bijdrage moet communicatie leveren aan het succes van dit project? Dit is het vertrekpunt voor alle volgende fasen.</div>
        <RichText
          inklapbaar
          value={content.communicatieopgave}
          onChange={(html) => update('communicatieopgave', html)}
          disabled={locked}
          placeholder="Formuleer de communicatieopgave…"
          minHeight={110}
        />
      </div>

      {/* Concept opslaan */}
      {!locked && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={saveDraft}
            disabled={saving}
            style={{ padding: '9px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Opslaan…' : 'Concept opslaan'}
          </button>
        </div>
      )}
        </div>{/* /LINKERKOLOM */}

        {/* RECHTERKOLOM — AI-ondersteuning */}
        <div>
          <div style={{ background: faseKleur(1).soft, border: `1px solid ${faseKleur(1).border}`, borderRadius: 12, padding: 18, position: isMobile ? 'static' : 'sticky', top: 16 }}>
            <div style={{ ...labelStyle, marginBottom: 2 }}>AI-ondersteuning</div>
            <div style={hintStyle}>AI signaleert blinde vlekken en stelt een concept-communicatieopgave voor. Jij beslist.</div>
            <button
              onClick={runAssist}
              disabled={aiLoading || locked}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '10px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: 'none', background: '#1e3a2f', color: '#fff', cursor: aiLoading || locked ? 'default' : 'pointer', opacity: aiLoading || locked ? 0.6 : 1 }}
            >
              {aiLoading ? 'AI denkt mee…' : 'Verhelderingsvragen ophalen'}
            </button>
            {ai && (
              <div style={{ marginTop: 16, display: 'grid', gap: 16 }}>
                {ai.verhelderingsvragen.length > 0 && (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e3a2f', marginBottom: 8 }}>Verhelderingsvragen — blinde vlekken om te bespreken</div>
                    <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
                      {ai.verhelderingsvragen.map((q, i) => (
                        <li key={i} style={{ fontSize: 13.5, color: '#2b3350', lineHeight: 1.5 }}>{q}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {ai.conceptCommunicatieopgave && (
                  <div style={{ background: '#fff', border: `1px solid ${faseKleur(1).border}`, borderRadius: 10, padding: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e3a2f', marginBottom: 6 }}>Concept-communicatieopgave (voorstel)</div>
                    <p style={{ margin: '0 0 12px', fontSize: 13.5, color: '#2b3350', lineHeight: 1.6 }}>{ai.conceptCommunicatieopgave}</p>
                    {!locked && (
                      <button onClick={applyConcept} style={{ padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: 600, border: 'none', background: '#1e3a2f', color: '#fff', cursor: 'pointer' }}>
                        Overnemen in communicatieopgave
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>{/* /grid */}

      {/* De Lock */}
      <LockBar
        status={status}
        onLock={doLock}
        onUnlock={doUnlock}
        lockedAt={lockedAt}
        canLock={canLock}
        busy={locking}
        faseLabel="Projectverkenning"
        canClear={canClear}
        onClear={doClear}
        clearing={clearing}
      />
      {!canLock && !locked && (
        <div style={{ marginTop: 8, fontSize: 12.5, color: '#8a90a0', textAlign: 'right' }}>
          Vul minimaal de doelstelling en de communicatieopgave in om te kunnen valideren.
        </div>
      )}
    </div>
  );
};
